import moment from 'moment';

import { queryGetAll } from './indexedDB';
import type { Entity, Group, User } from './types';

export function extractLocalizedText(value: unknown, preferredLanguage?: string): string {
	if (typeof value === 'string') return value;
	if (Array.isArray(value) && value.length > 0) {
		const lang = (entry: Record<string, unknown>) => entry?.language ?? entry?.languageID ?? '';
		if (preferredLanguage) {
			const match = value.find((entry) => String(lang(entry)).startsWith(preferredLanguage));
			if (match?.text) return String(match.text);
		}
		const en = value.find((entry) => String(lang(entry)).startsWith('en'));
		if (en?.text) return String(en.text);
		if (value[0]?.text) return String(value[0].text);
	}
	return '';
}

export function inGroup(entity: Entity, groupIDs: Set<string> | null): boolean {
	if (!groupIDs) return true;

	return (
		groupIDs.has(entity._department ?? '') ||
		entity._department_path.some((id) => groupIDs.has(id)) ||
		entity._teams.some((teamID) => groupIDs.has(teamID))
	);
}

export function isActiveUser(user: Entity, endDate: Date, userFilter?: string | null): boolean {
	const activated = user.activatedAt as Date | null;
	const deactivated = user.deactivatedAt as Date | null;

	if (user.excludeAnalytics) return false;
	if (user.isArchived) return false;
	if (userFilter !== 'activeAtEndDate') return true;
	if (activated && activated > endDate) return false;
	if (deactivated && deactivated <= endDate) return false;

	return true;
}

export function filterActiveUsersInGroup(
	users: Entity[],
	groupIDs: Set<string> | null,
	endDate: Date,
	userFilter: string,
): Entity[] {
	return users.filter((user) => {
		if (!inGroup(user, groupIDs) || !isActiveUser(user, endDate, userFilter)) {
			return false;
		}
		return true;
	});
}

const LABEL_FORMATS: Record<string, string> = {
	month: 'D MMM',
	quarter: 'MMM YYYY',
	year: 'MMM YYYY',
};

export function formatLabel(date: Date, interval: string | null): string {
	return moment(date).format(LABEL_FORMATS[interval ?? ''] ?? 'LL');
}

export function generateBuckets(startDate: Date, endDate: Date): { start: Date; end: Date }[] {
	const buckets: { start: Date; end: Date }[] = [];
	const rangeMs = endDate.getTime() - startDate.getTime();
	const unit = rangeMs < 62 * 86_400_000 ? 'week' : 'month';
	const current = moment(startDate).startOf(unit === 'week' ? 'isoWeek' : 'month');
	const endMoment = moment(endDate);
	while (current.isBefore(endMoment)) {
		const bucketStart = moment.max(current, moment(startDate)).toDate();
		const bucketEnd = moment.min(current.clone().add(1, unit), endMoment).toDate();
		buckets.push({ start: bucketStart, end: bucketEnd });
		current.add(1, unit);
	}
	return buckets;
}

export function displayFloat(value: number): string {
	return (Math.round(value * 10) / 10) % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
}

export function previousPeriod(
	interval: string | null,
	startDate: Date,
	endDate: Date,
): { startDate: Date; endDate: Date } {
	if (['month', 'quarter', 'year'].includes(interval ?? '')) {
		const prev = moment(startDate).subtract(1, interval as moment.unitOfTime.DurationConstructor);

		const period = interval as moment.unitOfTime.StartOf;

		return {
			startDate: prev.startOf(period).toDate(),
			endDate: prev.endOf(period).toDate(),
		};
	}
	const durationMs = endDate.getTime() - startDate.getTime();

	return {
		startDate: new Date(startDate.getTime() - durationMs),
		endDate: new Date(endDate.getTime() - durationMs),
	};
}

export function userName(user: User | undefined): string {
	if (!user) return '';
	return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
}

export function groupName(groupID: string | null, groups: Map<string, Group>): string {
	if (!groupID) return '';
	const group = groups.get(groupID);
	if (!group) return '';
	return extractLocalizedText(group.name);
}

export function toISOString(date: Date | null | undefined): string | null {
	if (!date) return null;
	return date.toISOString();
}

export async function resolveGroups(
	raw: string[] | undefined,
	db: IDBDatabase,
): Promise<Set<string> | null> {
	const filtered = (raw ?? []).filter(Boolean);
	if (filtered.length === 0) return null;

	const ids = new Set<string>();
	const recursive: string[] = [];

	filtered.forEach((entry) => {
		if (!entry.endsWith('+')) return ids.add(entry);
		const id = entry.slice(0, -1);
		ids.add(id);
		recursive.push(id);
	});

	if (recursive.length === 0) return ids;

	const groups = await queryGetAll<{ groupID: string; parentID?: string }>(db, 'Groups');
	const children = new Map<string, string[]>();

	groups.forEach((group) => {
		if (!group.parentID) return;
		const list = children.get(group.parentID);
		if (list) return list.push(group.groupID);
		children.set(group.parentID, [group.groupID]);
	});

	const stack = [...recursive];
	while (stack.length) {
		const id = stack.pop()!;
		(children.get(id) ?? []).forEach((child) => {
			if (ids.has(child)) return;
			ids.add(child);
			stack.push(child);
		});
	}

	return ids;
}
